import { useState } from 'react';
import { useInventory, useInventorySummary, useAdjustInventory } from '../hooks/useInventory';
import { useAccounts } from '../hooks/useAccounts';
import { useProducts } from '../hooks/useProducts';
import type { InventoryRecord, Account, Product } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function InventoryPage() {
  const { data: inventory, isLoading } = useInventory();
  const { data: summary } = useInventorySummary();
  const { data: accounts } = useAccounts();
  const { data: products } = useProducts();
  const adjustMutation = useAdjustInventory();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({ account_id: '', product_id: '', quantity_change: '', reason: '' });

  async function handleAdjust() {
    if (!adjustData.account_id || !adjustData.product_id || !adjustData.quantity_change || !adjustData.reason) {
      toast.error('All fields are required');
      return;
    }
    try {
      await adjustMutation.mutateAsync({
        account_id: Number(adjustData.account_id),
        product_id: Number(adjustData.product_id),
        quantity_change: Number(adjustData.quantity_change),
        reason: adjustData.reason,
      });
      toast.success('Inventory adjusted');
      setAdjustOpen(false);
      setAdjustData({ account_id: '', product_id: '', quantity_change: '', reason: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to adjust');
    }
  }

  // Summary is an array of objects with metal_type, total_oz, total_items, account_count
  const summaryItems = (summary as unknown as Array<{ metal_type: string; total_oz: number; total_items: number; account_count: number }>) ?? [];

  return (
    <div className="space-y-6">
      {/* Summary by metal */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryItems.map((s) => (
          <Card key={s.metal_type}>
            <p className="text-sm font-medium text-silver-500">{s.metal_type}</p>
            <p className="text-2xl font-bold text-silver-900">{Number(s.total_oz).toLocaleString()} oz</p>
            <p className="text-xs text-silver-400">
              {s.total_items} items across {s.account_count} accounts
            </p>
          </Card>
        ))}
      </div>

      {/* Inventory table */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-silver-500">{inventory?.length ?? 0} holdings</p>
        <Button onClick={() => setAdjustOpen(true)}>
          <AdjustmentsHorizontalIcon className="h-4 w-4" /> Manual Adjust
        </Button>
      </div>

      <Card padding={false}>
        <Table<InventoryRecord>
          loading={isLoading}
          data={inventory ?? []}
          columns={[
            { key: 'account_number', header: 'Account #' },
            { key: 'account_title', header: 'Account' },
            { key: 'product_code', header: 'Product Code' },
            { key: 'product_description', header: 'Product' },
            { key: 'metal_type', header: 'Metal', render: (r) => <Badge color="gold">{String(r.metal_type)}</Badge> },
            { key: 'quantity', header: 'Quantity', className: 'text-right font-mono', render: (r) => Number(r.quantity).toLocaleString() },
            { key: 'last_updated', header: 'Updated', render: (r) => r.last_updated ? new Date(r.last_updated).toLocaleDateString() : '-' },
          ]}
          emptyMessage="No inventory records. Create accounts and products to start tracking."
        />
      </Card>

      {/* Adjust Modal */}
      <Modal open={adjustOpen} onClose={() => setAdjustOpen(false)} title="Manual Inventory Adjustment">
        <div className="space-y-4">
          <Select
            label="Account"
            placeholder="Select account..."
            value={adjustData.account_id}
            onChange={(e) => setAdjustData({ ...adjustData, account_id: e.target.value })}
            options={(accounts ?? []).map((a: Account) => ({
              value: String(a.id), label: `${a.account_number} - ${a.title}`,
            }))}
          />
          <Select
            label="Product"
            placeholder="Select product..."
            value={adjustData.product_id}
            onChange={(e) => setAdjustData({ ...adjustData, product_id: e.target.value })}
            options={(products ?? []).map((p: Product) => ({
              value: String(p.id), label: `${p.product_code} - ${p.description}`,
            }))}
          />
          <Input
            label="Quantity Change"
            type="number"
            step="any"
            placeholder="Positive to add, negative to subtract"
            value={adjustData.quantity_change}
            onChange={(e) => setAdjustData({ ...adjustData, quantity_change: e.target.value })}
          />
          <Input
            label="Reason"
            placeholder="Reason for adjustment"
            value={adjustData.reason}
            onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button onClick={handleAdjust} loading={adjustMutation.isPending}>Adjust</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
